/**
 * 매출(본사 실매출) — 관리자 API 클라이언트
 *
 *  BE 엔드포인트(/api/admin/revenue) 와 1:1 매핑.
 *  본사 실매출 = ① 크레딧 충전(PG 실결제) + ② 온라인 이용권 서비스수수료.
 *  오프라인·취소·환불은 제외.
 */
import { apiClient } from './client';
import { API_ENDPOINTS } from '../../config/api';

/* ─────────────────────────────────────────────────────────────────
 *  타입 — BE AdminRevenueResponseDto 와 1:1
 * ───────────────────────────────────────────────────────────────── */

export interface MonthlyRevenue {
  ym: string; // yyyy-MM
  creditRevenue: number; // 크레딧 총액(PG 차감 전)
  creditPgFee: number; // 크레딧 PG수수료(추정)
  creditNet: number; // 크레딧 순매출(총액−PG)
  onlineFeeRevenue: number; // 실적재(본사 서비스수수료)
  onlineFeeStandard: number; // 표준율(0.9%) 환산
  onlinePgFee: number; // 온라인 PG수수료(토스 몫, 사업주 부담 — 본사 매출 아님)
  onlineGmv: number; // 온라인 결제금액
  revenue: number; // 실적재 본사 실매출(크레딧 순매출 + 온라인수수료)
  revenueStandard: number; // 표준율 환산 본사 실매출
}

export interface RevenueSummary {
  from: string;
  to: string;
  serviceFeeRate: number;
  pgFeeRate: number;
  totalCreditRevenue: number; // 크레딧 총액
  totalCreditPgFee: number; // 크레딧 PG수수료(추정)
  totalCreditNet: number; // 크레딧 순매출
  totalOnlineFeeRevenue: number;
  totalOnlineFeeStandard: number;
  totalOnlinePgFee: number;
  totalOnlineGmv: number;
  totalRevenue: number;
  totalRevenueStandard: number;
  months: MonthlyRevenue[];
}

export interface RevenueParams {
  from?: string; // yyyy-MM-dd
  to?: string; // yyyy-MM-dd
}

/** 전사 기본 수수료율 (INFO_FEE, COMPANY_SEQ=0) */
export interface FeeRates {
  pgFeeRate: number | null;
  serviceFeeRate: number | null;
  pgFeeUpdatedAt: string | null;
  serviceFeeUpdatedAt: string | null;
}

export interface FeeRatesUpdate {
  pgFeeRate: number;
  serviceFeeRate: number;
}

/* ─────────────────────────────────────────────────────────────────
 *  HTTP 헬퍼 — BaseResponse 에서 data 추출
 * ───────────────────────────────────────────────────────────────── */
const unwrap = <T,>(resp: { data?: any }): T => {
  if (resp?.data?.data !== undefined) return resp.data.data as T;
  return resp?.data as T;
};

const E = API_ENDPOINTS.ADMIN_REVENUE;

/* ─────────────────────────────────────────────────────────────────
 *  API 모듈
 * ───────────────────────────────────────────────────────────────── */
export const revenueApi = {
  /** 본사 실매출 (월별 + 합계). from/to 미지정 시 올해 1월~오늘. */
  async getSummary(params?: RevenueParams): Promise<RevenueSummary> {
    return unwrap<RevenueSummary>(await apiClient.get(E.SUMMARY, { params }));
  },

  /** 전사 기본 수수료율(PG·서비스) 조회. */
  async getFeeRates(): Promise<FeeRates> {
    return unwrap<FeeRates>(await apiClient.get(E.FEE_RATES));
  },

  /** 전사 기본 수수료율 저장. 이후 정산·크레딧 PG 차감·매출 표시에 적용. */
  async updateFeeRates(body: FeeRatesUpdate): Promise<FeeRates> {
    return unwrap<FeeRates>(await apiClient.put(E.FEE_RATES, body));
  },
};
